/* eslint-disable @typescript-eslint/no-unused-vars */
import { toast } from "react-hot-toast";
import { createStore } from "zustand";

import sleep from "utils/sleep";

import applySlashURL from "utils/applySlashURL";
import { envMapKeys } from "./constants";

let isFetching = false;

const getAwsUrl = () => {
  // @ts-ignore
  const url = window._env_.REACT_APP_API;
  if (url && url !== "" && url !== "undefined") {
    return url;
  }

  const replacedName = window.location.origin.includes("dashboard")
    ? "dashboard"
    : "admin";

  return window.location.origin.replace(replacedName, "foundation-api-admin");
};

type StoreConstants = ReturnType<typeof formatConstants>;

export type TEnvStore = {
  [K in keyof typeof envMapKeys]: string;
} & StoreConstants & {
    version: string | null;
    assignState: (envs: Record<string, string>) => void;
    fetchEnvs: (token?: string) => Promise<{
      keys: Record<string, string>;
      version: string;
    }>;
  };

export const evalShouldFetchEnvs = () => {
  if (isFetching) {
    return false;
  }

  if (
    !window.localStorage.getItem("envs") ||
    !window.localStorage.getItem("env_version")
  ) {
    return true;
  }

  const parsedEnvs = JSON.parse(window.localStorage.getItem("envs")!);
  const isEmpty = Object.keys(parsedEnvs).length === 0;
  if (isEmpty) {
    return true;
  }

  if (!isEmpty && !parsedEnvs.is_full_envs && localStorage.getItem("token")) {
    return true;
  }

  return false;
};

const formatEnvs = (_envs: Record<string, string>) => {
  const envs = Object.keys(envMapKeys).reduce(
    (acc, key) => {
      const { frontend, backend } = envMapKeys[key as keyof typeof envMapKeys];
      return {
        ...acc,
        [key]: _envs[backend] || import.meta.env[frontend],
      };
    },
    {} as Record<keyof typeof envMapKeys, string>,
  );

  const combinedKeys = {
    ...envs,
    ...formatConstants(envs),
  };

  return combinedKeys;
};

const getInitialEnvs = () => {
  const envs = JSON.parse(window.localStorage.getItem("envs") || "{}");
  return formatEnvs(envs);
};

const checkIfShouldRefresh = async (
  storeVersion: string,
  requestVersion: string,
) => {
  // Refresh if the version is different or doesn't exist in local storage
  const localVersion =
    window.localStorage.getItem("env_version") || storeVersion;

  const mustRefresh = (() => {
    if (!!localVersion && !isNaN(Number(localVersion))) {
      return Number(localVersion) !== Number(requestVersion);
    }

    return false;
  })();

  if (mustRefresh) {
    toast("Application config has been updated. Refreshing...");
    await sleep(1000);
    window.location.reload();
  }

  return false;
};

const formatConstants = (envs: Record<string, string>) => {
  const FOUNDATION_URL =
    import.meta.env.VITE_API_SERVICE === "heroku" ? envs.api : getAwsUrl();

  const baseUrl = applySlashURL(FOUNDATION_URL);

  const spreeUrl = applySlashURL(envs.spree_base_url);
  return {
    EVENT_LOCATION_DETAILS: {
      address: envs.address,
      lat: Number(envs.lat),
      lng: Number(envs.lng),
    },
    isDevelopment: envs.environment === "development",
    isStaging: envs.environment === "staging",
    isProduction: envs.environment === "production",
    USER_DASHBOARD_URL: envs.user_dashboard,
    URL_FOR_HEROKU: envs.api,
    FoundationURL: FOUNDATION_URL,
    urlMap: {
      v1: `${baseUrl}api/v1/`,
      v2: `${baseUrl}api/v2/`,
      v3: `${baseUrl}api/v3/`,
    },
    SpreeUrl: spreeUrl,
    spreeUrlMap: {
      v1: `${spreeUrl}api/v1/`,
    },
  };
};

export const createEnvStore = () => {
  const store = createStore<TEnvStore>((set, get) => {
    const initialEnvs = getInitialEnvs();
    return {
      ...initialEnvs,
      version: window.localStorage.getItem("env_version"),
      assignState: (envs) =>
        set((state) => ({
          ...state,
          ...formatEnvs(envs),
        })),
      fetchEnvs: async (token?: string) => {
        try {
          isFetching = true;
          const { urlMap } = get();

          const response = await fetch(
            urlMap.v3 + "/organizations/fetch-organization-config",
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            },
          );

          isFetching = false;

          if (response.ok === false) {
            throw new Error("Failed to fetch envs");
          }

          const data = await response.json();

          await checkIfShouldRefresh(get().version!, data.version);

          localStorage.setItem(
            "envs",
            JSON.stringify({ ...data.keys, is_full_envs: !!token }),
          );

          localStorage.setItem("env_version", data.version);
          get().assignState(data.keys);
          set({ version: data.version });
          return data;
        } catch (_e: any) {
          isFetching = false;
          throw new Error("Failed to fetch envs");
        }
      },
    };
  });

  const shouldFetchEnvs = evalShouldFetchEnvs();
  if (shouldFetchEnvs) {
    store.getState().fetchEnvs();
  }

  return store;
};
